import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Ledger, theme } from '@sg/garnish';

import { useLedgerItems } from '../../hooks/useLedgerItems';
import { usePaymentBreakdown } from '../../hooks/usePaymentBreakdown';
import { type OrderingLedger } from '../../machine/ordering-machine.types';

export const BagPriceSummary = (props: BagPriceSummaryProps) => {
  const {
    ledger,
    amountEligibleForFlex,
    flexPaymentMethod,
    stripePaymentMethod,
    tip,
    maxTipAmount,
    availableCredit,
    discountInfo,
    shouldUseCredit,
    shouldUseFlex,
  } = props;

  const { stripeAmount, flexAmount } = usePaymentBreakdown({
    ledger,
    tip,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
  });

  const ledgerItems = useLedgerItems({
    ledger,
    tip,
    maxTipAmount,
    discountInfo,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
  });

  return (
    <Ledger.Container style={styles.container}>
      <View style={styles.content}>
        <BodyText sizeMatch={['14']} bold style={styles.title}>
          <FormattedMessage {...messages.title} />
        </BodyText>

        {ledgerItems.filter(Boolean).map((item) => (
          <Ledger.Row key={item.label} sizeMatch={['14']} {...item} />
        ))}
      </View>

      {amountEligibleForFlex > 0 && shouldUseFlex && stripePaymentMethod && flexPaymentMethod ? (
        <View style={[styles.content, styles.paymentSplit]}>
          <BodyText sizeMatch={['12']} bold>
            <FormattedMessage {...messages.split} />
          </BodyText>

          <Ledger.Row label={flexPaymentMethod} amount={flexAmount} sizeMatch={['14']} />

          <Ledger.Row label={stripePaymentMethod} amount={stripeAmount} sizeMatch={['14']} />
        </View>
      ) : null}
    </Ledger.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Price Summary',
    description: 'Bag | Price Summary | Title',
  },
  split: {
    defaultMessage: 'Payment split between two cards',
    description: 'Bag | Price Summary | Split',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.OPACITY.OATMEAL.DARK,
    borderRadius: theme.radius.large,
    paddingTop: theme.spacing['2'],
    paddingBottom: 0,
  },
  content: {
    paddingVertical: theme.spacing['4'],
    paddingHorizontal: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  paymentSplit: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.OPACITY.LINEN.DARK,
  },
  title: {
    textAlign: 'center',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagPriceSummaryProps = {
  ledger?: OrderingLedger;
  amountEligibleForFlex: number;
  flexPaymentMethod: string | null;
  stripePaymentMethod?: string;
  discountInfo?: string;
  tip: number;
  maxTipAmount: number;
  availableCredit: number;
  shouldUseCredit: boolean;
  shouldUseFlex: boolean;
};
